import { Card, DeprecatedTable, Sizes, Spacings } from "@sede-enterprise/shell-ds-react-framework";
import type {
  ColumnsType,
  RecordType,
} from "@sede-enterprise/shell-ds-react-framework/build/esm/components/DeprecatedTable/Table.types";
import type { ReactElement, ReactNode } from "react";
import styled from "styled-components";
import {
  EQUITY_SCOPE_CARBON_PEAK_ANNUAL_EMISSION,
  KPI_SUMMARY,
  MARGIN_TO_CARBON_RATIO_AVG,
  NET_ABS_EMISSION_SCOPE_PEAK_ANNUAL_EMISSION,
} from "../../api/constants";
import { eighty, twofifty } from "../../constants";
import type { IGroupNCI, IKpiCardData, IKpiCardDataColumnObject } from "../../types/types";
import {
  getColorByValue,
  getImpactNciDataForTwoYears,
} from "../AllDeals/DealDetails/DealDetailFunctions";

const StyledBaseTable = styled(DeprecatedTable)<{
  rowColor: { rowColorForFirstRow: string; rowColorForSecondRow: string };
}>`
  tbody tr:nth-child(3) td {
    border-bottom: 1px solid #e0e0e0;
  }
  tbody tr:nth-child(4) td {
    color: ${(props) => props.rowColor.rowColorForFirstRow};
    font-weight: bold;
    font-size: 14px;
  }
  tbody tr:nth-child(5) td {
    color: ${(props) => props.rowColor.rowColorForSecondRow};
    font-weight: bold;
    font-size: 14px;
  }
`;
export const KpiCardView = (props: {
  cardTableData: IKpiCardData[];
  impactNciData: IGroupNCI[];
  isTSProducts?: boolean;
  isSLMT?: boolean;
}) => {
  const cardCols: string[] = ["KPIs", "Value", "Unit"];
  const cardTableCols = () => {
    const cardColObj: IKpiCardDataColumnObject[] = [];
    cardCols.forEach((val: string | number) => {
      // render cells with disabled styling for T&S Products when KPI is Equity Scope
      const renderCell = (text: ReactNode, record: IKpiCardData): ReactElement => {
        const kpiName = record?.KPIs as string;
        const isUnitCol = String(val) === "Unit";
        // For T&S Products, force Net Absolute Emissions unit to 'ktCO2e'
        if (
          props.isTSProducts &&
          isUnitCol &&
          kpiName === NET_ABS_EMISSION_SCOPE_PEAK_ANNUAL_EMISSION
        ) {
          return <>ktCO2e</>;
        }
        // Disable Equity Scope for T&S Products; disable MCR row for SLMT
        const shouldDisableTs =
          props.isTSProducts && kpiName === EQUITY_SCOPE_CARBON_PEAK_ANNUAL_EMISSION;
        const shouldDisableSlmt = props.isSLMT && kpiName === MARGIN_TO_CARBON_RATIO_AVG;
        if (!shouldDisableTs && !shouldDisableSlmt) return <>{text}</>;
        return <span style={{ color: "#9e9e9e" }}>{text}</span>;
      };

      cardColObj.push({
        dataIndex: val,
        width: val === "KPIs" ? twofifty : eighty,
        key: val,
        title: val,
        className: "leftAlign",
        render: renderCell,
      });
    });
    return cardColObj;
  };
  const {
    nciValueForFirstRow,
    nciValueForSecondRow,
    nciForFirstRowUnit,
    nciForSecondRowUnit,
    firstRowYear,
    secondRowYear,
  } = getImpactNciDataForTwoYears(props.impactNciData);

  const decimals = props.isTSProducts ? 5 : 3;
  const formatNciValue = (value: string | undefined, decimalPlaces: number) => {
    if (value === undefined || value === null) return "NA";
    if (value === "NA") return "NA";
    const num = Number(String(value).replace(/,/g, ""));
    if (Number.isNaN(num)) return String(value);
    return num.toFixed(decimalPlaces);
  };
  const formattedFirstRowValue = formatNciValue(nciValueForFirstRow, decimals);
  const formattedSecondRowValue = formatNciValue(nciValueForSecondRow, decimals);
  const cardDataWithImpactNci = [
    ...props.cardTableData,
    ...(firstRowYear
      ? [
          {
            KPIs: `NCI Impact in ${firstRowYear}`,
            Value: formattedFirstRowValue,
            Unit: nciForFirstRowUnit,
          },
        ]
      : []),
    {
      KPIs: `NCI Impact in ${secondRowYear}`,
      Value: formattedSecondRowValue,
      Unit: nciForSecondRowUnit,
    },
  ];
  const rowColorForFirstRow = getColorByValue(Number(nciValueForFirstRow));
  const rowColorForSecondRow = getColorByValue(Number(nciValueForSecondRow));
  return (
    <Card
      size={Sizes.Small}
      bodyPadding={true}
      header={{
        title: KPI_SUMMARY,
      }}
      className="kpiCardMaxWidth"
      padding={Spacings.Tight}
    >
      <StyledBaseTable
        size="small"
        data={cardDataWithImpactNci}
        columns={cardTableCols() as ColumnsType<RecordType>}
        tableLayout="fixed"
        rowKey={"KPIs"}
        wrapperStyle={{
          textAlign: "left",
        }}
        rowColor={{ rowColorForFirstRow, rowColorForSecondRow }}
      />
    </Card>
  );
};
